import { useRef, useState } from "react";
import { Plus, Pencil, Trash2, FileSpreadsheet, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { api, download, errMsg } from "../lib/api";
import { toast } from "sonner";
import { fdate, fmt, money, num, run, useLoad } from "../lib/util";
import { Alert, Badge, Btn, Card, Field, Input, ProductSelect, SectionTitle, Table, Empty, cellCls } from "../components/kit";
import { useSchool } from "./ctx";
import { Downloads } from "./Forms";

function ContractDialog({ initial, onClose, onSaved }) {
  const { products } = useSchool();
  const [f, setF] = useState({ number: "", supplier: "", supplier_doc: "", start_date: "", end_date: "", items: [], ...initial });
  const setItem = (i, v) => setF({ ...f, items: f.items.map((x, j) => (j === i ? v : x)) });
  const save = async () => {
    const body = { number: f.number, supplier: f.supplier, supplier_doc: f.supplier_doc || null, start_date: f.start_date, end_date: f.end_date, version: f.version ?? null,
      items: f.items.map((x) => ({ product_id: x.product_id || null, description: x.description || null, unit: x.unit || null, qty: x.qty ?? 0, factor: x.factor || 1, unit_price: x.unit_price ?? null })) };
    if (await run(() => (f.id ? api.put(`/s/contracts/${f.id}`, body) : api.post("/s/contracts", body)), "Contrato salvo")) { onSaved(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Editar contrato" : "Cadastrar contrato"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Número"><Input data-testid="contract-number-input" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} /></Field>
          <Field label="Fornecedor / agricultor / cooperativa" className="sm:col-span-2"><Input data-testid="contract-supplier-input" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} /></Field>
          <Field label="CNPJ/CPF"><Input value={f.supplier_doc || ""} onChange={(e) => setF({ ...f, supplier_doc: e.target.value })} /></Field>
          <Field label="Data inicial"><Input data-testid="contract-start-input" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
          <Field label="Data final"><Input data-testid="contract-end-input" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
        </div>
        <table className="w-full text-sm mapia-table border mt-2">
          <thead><tr><th>Produto do estoque</th><th>Descrição</th><th>Un.</th><th>Qtd. contratada</th><th>Fator p/ estoque</th><th>V. unit.</th><th /></tr></thead>
          <tbody>
            {f.items.map((x, i) => (
              <tr key={i}>
                <td><ProductSelect testid={`contract-item-product-${i}`} products={products} value={x.product_id} allowCreate createDefaults={{ name: x.description || "", unit: x.unit || "kg" }} onChange={(id) => setItem(i, { ...x, product_id: id })} /></td>
                <td><input className={cellCls} value={x.description || ""} onChange={(e) => setItem(i, { ...x, description: e.target.value })} /></td>
                <td><input className={`${cellCls} max-w-[70px]`} value={x.unit || ""} onChange={(e) => setItem(i, { ...x, unit: e.target.value })} /></td>
                <td><input data-testid={`contract-item-qty-${i}`} type="number" step="any" className={cellCls} value={x.qty ?? ""} onChange={(e) => setItem(i, { ...x, qty: num(e.target.value) })} /></td>
                <td><input type="number" step="any" className={cellCls} value={x.factor ?? 1} onChange={(e) => setItem(i, { ...x, factor: num(e.target.value) })} /></td>
                <td><input type="number" step="any" className={cellCls} value={x.unit_price ?? ""} onChange={(e) => setItem(i, { ...x, unit_price: num(e.target.value) })} /></td>
                <td><button onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-red-500" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Btn variant="outline" data-testid="contract-add-item" onClick={() => setF({ ...f, items: [...f.items, { qty: null, factor: 1 }] })}><Plus className="w-4 h-4" />Adicionar produto</Btn>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="contract-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriorImport({ c, onDone }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const tpl = () => download(`/s/contracts/${c.id}/prior-template`, `compras_anteriores_${c.number.replace("/", "-")}.xlsx`).catch((e) => toast.error(e.message));
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = (await api.post(`/s/contracts/${c.id}/prior-import`, fd)).data;
      toast.success(`${r.imported} produto(s) com compras anteriores importados`);
      if (r.unmatched.length) toast.warning(`Não importados: ${r.unmatched.join("; ")}`, { duration: 12000 });
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <>
      <Btn variant="outline" data-testid={`contract-prior-template-${c.number}`} onClick={tpl}><FileSpreadsheet className="w-4 h-4" />Modelo Excel</Btn>
      <Btn variant="outline" disabled={busy} data-testid={`contract-prior-import-${c.number}`} onClick={() => ref.current.click()}><Upload className="w-4 h-4" />{busy ? "Importando…" : "Importar compras anteriores"}</Btn>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" data-testid={`contract-prior-file-${c.number}`} onChange={(e) => send(e.target.files[0])} />
    </>
  );
}

function Members({ c, onDone }) {
  const [list, setList] = useState(c.members || []);
  const [f, setF] = useState({ name: "", doc: "" });
  const save = async (members) => {
    const r = await run(() => api.put(`/s/contracts/${c.id}/members`, { members }), "Agricultores salvos");
    if (r) { setList(r.data.members); setF({ name: "", doc: "" }); if (r.data.linked) toast.success(`${r.data.linked} nota(s) vinculada(s) automaticamente`); onDone(); }
  };
  return (
    <div className="mt-4 rounded-lg bg-slate-50 p-3" data-testid={`contract-members-${c.number}`}>
      <div className="font-semibold text-sm mb-1">Agricultores do grupo</div>
      <p className="text-xs text-slate-500 mb-2">Notas emitidas por estes agricultores (nome ou CPF) dentro da vigência são vinculadas a este contrato automaticamente.</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.map((m, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white border px-3 py-1 text-xs" data-testid={`member-${i}`}>
            {m.name}{m.doc && ` · ${m.doc}`}
            <button data-testid={`member-remove-${i}`} onClick={() => save(list.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3 text-red-500" /></button>
          </span>
        ))}
        {!list.length && <span className="text-xs text-slate-400">Nenhum agricultor cadastrado.</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input data-testid={`member-name-${c.number}`} placeholder="Nome do agricultor" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="max-w-xs" />
        <Input data-testid={`member-doc-${c.number}`} placeholder="CPF" value={f.doc} onChange={(e) => setF({ ...f, doc: e.target.value })} className="max-w-[180px]" />
        <Btn variant="outline" data-testid={`member-add-${c.number}`} disabled={f.name.trim().length < 3} onClick={() => save([...list, f])}><Plus className="w-4 h-4" />Adicionar</Btn>
      </div>
    </div>
  );
}

const today = () => new Date().toLocaleDateString("sv-SE");
const isEmptyActive = (c) => !c.execution.length && c.start_date <= today() && today() <= c.end_date;

function EmptyContracts({ contracts, onEdit }) {
  const list = contracts.filter(isEmptyActive);
  if (!list.length) return null;
  return (
    <Alert tone="amber" title="Contrato em vigor sem produtos cadastrados" testid="agro-empty-contracts">
      <div className="mb-1">As notas da agricultura familiar não são ligadas a contratos sem produtos e o saldo a comprar não é controlado. Cadastre os produtos ou remova o contrato.</div>
      {list.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2">
          <span>Contrato {c.number} · {c.supplier} (vigência {fdate(c.start_date)} a {fdate(c.end_date)})</span>
          <button type="button" className="underline font-semibold" data-testid={`empty-contract-edit-${c.number}`} onClick={() => onEdit(c)}>Cadastrar produtos</button>
        </div>
      ))}
    </Alert>
  );
}

function AfNotes({ contracts, onChange }) {
  const [notes, reload] = useLoad(async () => (await api.get("/s/af-notes")).data, [contracts]);
  const link = async (n, cid) => {
    if (await run(() => api.put(`/s/af-notes/${n.id}/contract`, { contract_id: cid || null }), cid ? "Nota vinculada ao contrato" : "Vínculo removido")) { reload(); onChange(); }
  };
  if (!notes?.length) return null;
  return (
    <Card data-testid="af-notes-card">
      <h3 className="font-heading font-bold text-[#0B192C] mb-1">Notas da agricultura familiar</h3>
      <p className="text-xs text-slate-500 mb-3">Vincule cada nota confirmada ao contrato para descontar do saldo a comprar.</p>
      <Table>
        <thead><tr><th>Nota</th><th>Fornecedor</th><th>Emissão</th><th className="num">Itens</th><th className="num">Valor</th><th>Contrato</th></tr></thead>
        <tbody>
          {notes.map((n) => (
            <tr key={n.id} data-testid={`af-note-${n.number}`}>
              <td className="font-mono">{n.number}</td>
              <td>{n.supplier}</td>
              <td>{fdate(n.issue_date)}</td>
              <td className="num">{n.items}</td>
              <td className="num">{money(n.total)}</td>
              <td>
                <select data-testid={`af-note-contract-${n.number}`} className={cellCls + " py-1.5"} value={n.contract_id || ""} onChange={(e) => link(n, e.target.value)}>
                  <option value="">— sem contrato —</option>
                  {contracts.map((c) => <option key={c.id} value={c.id}>{`${c.number} · ${c.supplier}`}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

export default function Agro() {
  const { tick, bump, setSection } = useSchool();
  const [ex, reload] = useLoad(async () => (await api.get("/s/contracts-execution")).data, [tick]);
  const [edit, setEdit] = useState(null);
  const del = async (c) => {
    if (window.confirm(`Remover o contrato ${c.number}?`) && (await run(() => api.delete(`/s/contracts/${c.id}`), "Contrato removido"))) { reload(); bump(); }
  };
  return (
    <div className="space-y-5">
      <SectionTitle title="Controle de compras da agricultura familiar" subtitle="A comprar = contratado − comprado antes (importado do Excel) − comprado nas notas vinculadas, em toda a vigência. Compras do fornecedor principal não descontam estes contratos.">
        <Btn data-testid="new-contract-button" onClick={() => setEdit({})}><Plus className="w-4 h-4" />Cadastrar contrato</Btn>
        <Btn variant="outline" onClick={() => setSection("doc:contrato_af")}>Importar contrato</Btn>
        <Downloads form="agricultura" label="controle AF" />
      </SectionTitle>
      {ex?.flags.length > 0 && <Alert tone="red" title="Sinalizações" testid="agro-flags">{ex.flags.map((f, i) => <div key={i}>{f.message}</div>)}</Alert>}
      <EmptyContracts contracts={ex?.contracts || []} onEdit={setEdit} />
      {(ex?.contracts || []).map((c) => (
        <Card key={c.id} data-testid={`contract-card-${c.number}`}>
          <div className="flex flex-wrap justify-between gap-3 mb-3">
            <div>
              <h3 className="font-heading font-bold text-lg text-[#0B192C]">Contrato {c.number} · {c.supplier}</h3>
              <div className="text-xs text-slate-500">{c.supplier_doc ? `${c.supplier_doc} · ` : ""}Vigência {fdate(c.start_date)} a {fdate(c.end_date)} {c.document_id && <Badge tone="info">importado</Badge>} {isEmptyActive(c) && <Badge tone="amber" data-testid={`contract-empty-badge-${c.number}`}>sem produtos</Badge>}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <PriorImport c={c} onDone={() => { reload(); bump(); }} />
              <Btn variant="outline" onClick={() => setEdit(c)}><Pencil className="w-4 h-4" />Editar</Btn>
              <Btn variant="danger" onClick={() => del(c)}><Trash2 className="w-4 h-4" /></Btn>
            </div>
          </div>
          <Table>
            <thead><tr><th>Produto</th><th>Un. contrato</th><th className="num">Contratado (un. estoque)</th><th className="num">Comprado antes</th><th className="num">Comprado nas notas</th><th className="num">A comprar</th><th>% executado</th><th className="num">V. unit.</th></tr></thead>
            <tbody>
              {c.execution.map((r, i) => (
                <tr key={i} className={r.remaining < 0 ? "!bg-red-50" : ""}>
                  <td className="font-semibold">{r.product}{!r.product_id && <Badge tone="amber">sem produto</Badge>}</td>
                  <td>{r.unit} {r.factor !== 1 && <span className="text-xs text-slate-400">×{fmt(r.factor)}</span>}</td>
                  <td className="num">{fmt(r.contracted)}</td>
                  <td className="num" data-testid={`contract-prior-${c.number}-${i}`}>{fmt(r.prior)}</td>
                  <td className="num">{fmt(r.bought_nf)}</td>
                  <td className={`num font-bold ${r.remaining < 0 ? "text-red-700" : ""}`}>{fmt(r.remaining)}</td>
                  <td className="min-w-[140px]">{r.pct == null ? "—" : <div className="flex items-center gap-2"><Progress value={Math.min(100, r.pct)} className="h-2" /><span className="text-xs font-mono">{fmt(r.pct)}%</span></div>}</td>
                  <td className="num">{money(r.unit_price)}</td>
                </tr>
              ))}
              {!c.execution.length && <tr><td colSpan={8} className="text-center text-sm text-slate-500" data-testid={`contract-empty-row-${c.number}`}>Nenhum produto com quantidade cadastrada neste contrato.</td></tr>}
            </tbody>
          </Table>
          <Members c={c} onDone={() => { reload(); bump(); }} />
        </Card>
      ))}
      {ex && <AfNotes contracts={ex.contracts} onChange={() => { reload(); bump(); }} />}
      {ex && !ex.contracts.length && <Empty>Nenhum contrato cadastrado.</Empty>}
      {edit && <ContractDialog initial={edit} onClose={() => setEdit(null)} onSaved={() => { reload(); bump(); }} />}
    </div>
  );
}
